import { Component } from 'react';
import {
    filter,
    every,
    some,
    isEqual,
    get,
    head,
    isEmpty
} from 'lodash';
import { hasSameValueCaseInsensitive } from 'common/util/hasSameValueCaseInsensitive';

import DataSourceValueLoader from 'common/containers/DataSourceValueLoader/DataSourceValueLoader';
import AutocompleteDataSource from 'common/components/AutocompleteDataSource/AutocompleteDataSource';
import DataTypeEditor from 'common/components/DataTypeEditor/DataTypeEditor';
import ValueTags from 'common/components/ValueTags/ValueTags';
import styles from '../ValueSets.scss';

class DataSourceMultipleValue extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editorValue: '',
            hasError: false,
            errorMessage: null
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (!isEqual(prevProps.validationErrors, this.props.validationErrors)) {
            this.setState({
                errorMessage: get(head(this.props.validationErrors), 'message')
            });
        }
    }

    onEditorValueChange(value) {
        this.setState({
            editorValue: value
        });
    }

    isSelectedValueValid() {
        const {
            hasError,
            editorValue,
        } = this.state;
        const { value: selectedValues } = this.props;

        const isValueSelected = some(selectedValues, selectedValue => selectedValue === editorValue);

        return !hasError && Boolean(editorValue) && !isValueSelected;
    }

    onValueSelect() {
        const { editorValue } = this.state;

        const isValid = this.isSelectedValueValid();
        if (!isValid) {
            return this.setState({
                errorMessage: `${editorValue} is not a value from the list`
            });
        }

        const {
            changeHandler,
            value: selectedValues
        } = this.props;

        const newValues = [
            ...(selectedValues || []),
            editorValue
        ];

        this.setState({
            editorValue: '',
            hasError: false
        });
        changeHandler(newValues);
    }

    onValueRemove(valueToRemove) {
        const {
            changeHandler,
            value: selectedValues
        } = this.props;

        const newValues = filter(selectedValues, selectedValue => selectedValue !== valueToRemove);
        changeHandler(newValues);
    }

    onKeyPress(evt) {
        if (evt.key === 'Enter') {
            evt.stopPropagation();
            this.onValueSelect();
        }
    }

    onValidStateChange(autocompleteSource, { hasError, editorValue }) {
        this.setState({
            hasError,
            errorMessage: null
        });
    }

    shouldShowError() {
        const { hasError } = this.state;
        const {
            value: selectedValues,
            validationErrors
        } = this.props;

        return hasError || (isEmpty(selectedValues) && !isEmpty(validationErrors));
    }

    render() {
        const {
            value: selectedValues = [],
            dataType,
            dataSourceId
        } = this.props;
        const { errorMessage } = this.state;

        return (
            <div className={styles.container} onKeyPress={this.onKeyPress.bind(this)} data-cy="dataSourceMultipleValue">
                <ValueTags
                    values={selectedValues}
                    onRemove={this.onValueRemove.bind(this)}/>
                {this.shouldShowError() &&
                <span className={styles.errorMessage}>{errorMessage}</span>
                }
                <DataEntry
                    onBlur={this.onValueSelect.bind(this)}
                    onChange={this.onEditorValueChange.bind(this)}
                    dataType={dataType}
                    hasError={this.shouldShowError()}
                    selectedValues={selectedValues}
                    dataSourceId={dataSourceId}
                    onValidStateChange={this.onValidStateChange.bind(this)}
                    value={this.state.editorValue}/>
            </div>
        );
    }
}

function ValuePicker({
    dataSourceItems,
    selectedValues = [],
    value,
    onBlur,
    onChange,
    dataType,
    onValidStateChange,
    hasError
}) {
    const availableValues = filter(
        dataSourceItems,
        (dataSourceItem) => every(selectedValues,
            selectedValue =>
                !hasSameValueCaseInsensitive(selectedValue, dataSourceItem.value)
        )
    );

    return (
        <AutocompleteDataSource
            values={availableValues}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            dataType={dataType}
            validate={true}
            selectedValues={selectedValues}
            onValidStateChange={onValidStateChange}>
            <DataTypeEditor
                className={`${styles.dataTypeEditor} ${hasError && styles.dataTypeEditorError}`}/>
        </AutocompleteDataSource>
    );
}

function DataEntry({
    selectedValues,
    value,
    onChange,
    onBlur,
    dataType,
    onValidStateChange,
    dataSourceId,
    hasError
}) {
    return (
        <DataSourceValueLoader dataSourceId={dataSourceId}>
            <ValuePicker
                selectedValues={selectedValues}
                value={value}
                hasError={hasError}
                onChange={onChange}
                onBlur={onBlur}
                dataType={dataType}
                onValidStateChange={onValidStateChange}/>
        </DataSourceValueLoader>
    );
}

export default DataSourceMultipleValue;
